import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Image,
  Mic,
  MessageSquare,
  HardDrive,
  Receipt,
  DollarSign,
  FileCheck,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Modal, ConfirmModal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard, StatCardGrid } from '@/components/widgets/StatCard';
import {
  useUser,
  useChangePlan,
  useCancelSubscription,
  useExtendTrial,
} from '@/hooks/useUsers';
import { usePlans } from '@/hooks/usePlans';

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
    active: 'success',
    trial: 'warning',
    canceled: 'danger',
    expired: 'danger',
    past_due: 'warning',
  };

  const labels: Record<string, string> = {
    active: 'Ativo',
    trial: 'Trial',
    canceled: 'Cancelado',
    expired: 'Expirado',
    past_due: 'Pagamento Pendente',
  };

  return (
    <Badge variant={variants[status || ''] || 'default'} size="lg">
      {labels[status || ''] || status || 'N/A'}
    </Badge>
  );
};

const formatCurrency = (amount: number, currency: string = 'BRL') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-GB', {
    style: 'currency',
    currency,
  }).format(amount);
};

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading } = useUser(id!);
  const { data: plans } = usePlans();
  const changePlanMutation = useChangePlan();
  const cancelMutation = useCancelSubscription();
  const extendTrialMutation = useExtendTrial();

  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [trialDays, setTrialDays] = useState(7);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Usuario nao encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mt-4">
          Voltar
        </Button>
      </div>
    );
  }

  // Cast to AdminUserDetail to access optional extended fields
  const userDetail = user as any;
  const subscription = userDetail.subscription;
  const stats = userDetail.stats;
  const usage = userDetail.currentMonthUsage;
  const billingHistory = userDetail.billingHistory;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Detalhes do Usuario
        </h1>
      </div>

      {/* User Info Card */}
      <Card>
        <CardContent>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {user.name?.charAt(0) || user.whatsappName?.charAt(0) || user.phoneNumber?.charAt(2) || '?'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {user.name || user.whatsappName || 'Sem nome'}
                  </h2>
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <span className="text-lg">{user.phoneCountry === 'BR' ? '🇧🇷' : '🇬🇧'}</span>
                    <span>{user.phoneCountry === 'BR' ? 'Brasil' : 'Reino Unido'}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Phone className="w-4 h-4" />
                  <span className="font-mono">{user.phoneNumber || 'N/A'}</span>
                </div>
                {user.email && (
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Mail className="w-4 h-4" />
                    <span>{user.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Desde {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {subscription && (
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Plano atual</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                    {subscription.plan}
                  </p>
                  <StatusBadge status={subscription.status} />
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowChangePlanModal(true)}
                >
                  Mudar Plano
                </Button>
                {subscription?.status === 'trial' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowExtendTrialModal(true)}
                  >
                    Estender Trial
                  </Button>
                )}
                {subscription?.status === 'active' && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Estatisticas
        </h3>
        <StatCardGrid columns={4}>
          <StatCard
            title="Transacoes"
            value={stats?.transactions?.total || 0}
            icon={<Receipt className="w-5 h-5" />}
            variant="default"
          />
          <StatCard
            title="Valor Total"
            value={formatCurrency(stats?.transactions?.totalAmount || 0, user.phoneCountry === 'BR' ? 'BRL' : 'GBP')}
            icon={<DollarSign className="w-5 h-5" />}
            variant="default"
          />
          <StatCard
            title="Recibos"
            value={stats?.receipts?.total || 0}
            icon={<Image className="w-5 h-5" />}
            variant="default"
          />
          <StatCard
            title="Processados"
            value={stats?.receipts?.processed || 0}
            icon={<FileCheck className="w-5 h-5" />}
            variant="default"
          />
        </StatCardGrid>
      </div>

      {/* Usage Stats (if available) */}
      {usage && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Uso do Mes Atual
          </h3>
          <StatCardGrid columns={4}>
            <StatCard
              title="Fotos"
              value={usage.photosCount || 0}
              icon={<Image className="w-5 h-5" />}
              variant="default"
            />
            <StatCard
              title="Audios"
              value={usage.audiosCount || 0}
              icon={<Mic className="w-5 h-5" />}
              variant="default"
            />
            <StatCard
              title="Textos"
              value={usage.textsCount || 0}
              icon={<MessageSquare className="w-5 h-5" />}
              variant="default"
            />
            <StatCard
              title="Storage"
              value={formatBytes(usage.storageUsedBytes || 0)}
              icon={<HardDrive className="w-5 h-5" />}
              variant="default"
            />
          </StatCardGrid>
        </div>
      )}

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Historico de Billing
          </CardTitle>
        </CardHeader>
        <CardContent>
          {billingHistory && billingHistory.length > 0 ? (
            <div className="space-y-3">
              {billingHistory.map((event: any) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-secondary-700 last:border-0"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {event.type === 'payment_succeeded' && 'Pagamento'}
                      {event.type === 'payment_failed' && 'Pagamento Falhou'}
                      {event.type === 'subscription_created' && 'Assinatura Criada'}
                      {event.type === 'subscription_canceled' && 'Assinatura Cancelada'}
                      {event.type === 'trial_started' && 'Trial Iniciado'}
                      {event.type === 'trial_ended' && 'Trial Encerrado'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(event.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-right">
                    {event.amount && (
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(event.amount, event.currency)}
                      </p>
                    )}
                    <Badge
                      variant={event.status === 'succeeded' ? 'success' : 'danger'}
                      size="sm"
                    >
                      {event.status === 'succeeded' ? 'Sucesso' : 'Falhou'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              Nenhum evento de billing
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <Modal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        title="Mudar Plano"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowChangePlanModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                changePlanMutation.mutate({ userId: id!, planId: selectedPlanId });
                setShowChangePlanModal(false);
              }}
              isLoading={changePlanMutation.isPending}
              disabled={!selectedPlanId}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <Select
          label="Selecione o novo plano"
          options={[
            { value: '', label: 'Selecione...' },
            ...(plans || []).map(plan => ({
              value: plan.id,
              label: `${plan.name} - ${formatCurrency(parseFloat(plan.priceMonthly), plan.currency)}/mes`,
            })),
          ]}
          value={selectedPlanId}
          onChange={(e) => setSelectedPlanId(e.target.value)}
        />
      </Modal>

      <Modal
        isOpen={showExtendTrialModal}
        onClose={() => setShowExtendTrialModal(false)}
        title="Estender Trial"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowExtendTrialModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                extendTrialMutation.mutate({ userId: id!, days: trialDays });
                setShowExtendTrialModal(false);
              }}
              isLoading={extendTrialMutation.isPending}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <Input
          type="number"
          label="Dias para estender"
          value={trialDays}
          onChange={(e) => setTrialDays(parseInt(e.target.value) || 7)}
          min={1}
          max={30}
        />
      </Modal>

      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => {
          cancelMutation.mutate({ userId: id!, immediate: false });
          setShowCancelModal(false);
        }}
        title="Cancelar Assinatura"
        message="Tem certeza que deseja cancelar a assinatura deste usuario? O acesso sera mantido ate o final do periodo atual."
        confirmText="Cancelar Assinatura"
        variant="danger"
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
};

export default UserDetailPage;
